import React, { useState, useRef, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig, baseURL } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import "../../styles/CategoryProductList.css";
import { useNavigate } from "react-router-dom";

const CustomerTypeRadio = ({
  label,
  value,
  isSelected,
  onSelect,
  disabled = false,
}) => (
  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
    <label
      className="d-flex align-items-center cursor-pointer"
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <input
        type="radio"
        className="form-check-input me-2"
        name="customerType"
        value={value}
        checked={isSelected}
        onChange={onSelect}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  </div>
);

const AddNotification = () => {
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [display, setDisplay] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [productId, setProductId] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [fetchingCategory, setFetchingCategory] = useState(true);
  const [fetchingBrand, setFetchingBrand] = useState(false);
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [status, setStatus] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageChanged, setImageChanged] = useState(false);
  const navigate = useNavigate();
  const mainImageElement = useRef();
  const toast = useNotification();
  const urlParams = new URLSearchParams(window.location.search);
  const notificationIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!notificationIdFromUrl && !isViewMode;
  const [notificationId, setNotificationId] = useState(
    notificationIdFromUrl ? parseInt(notificationIdFromUrl, 10) : null,
  );
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [selectedCustomerType, setSelectedCustomerType] = useState("");
  const [customerGroup, setCustomerGroup] = useState("");

  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary
      ? {
          backgroundColor: saving ? "#1565c0" : "#1976d2",
          color: "#fff",
        }
      : {
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff",
          color: saving ? "#9e9e9e" : "#1976d2",
        }),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`}
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  // ✅ Fetch Categories on mount
  useEffect(() => {
    setFetchingCategory(true);
    api
      .get("/category/", adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          setCategoryList(res.data.data || []);
        }
      })
      .catch(() => setCategoryList([]))
      .finally(() => setFetchingCategory(false));
  }, []);

  // ✅ Fetch Brands when Category changes (Single Select Logic)
  useEffect(() => {
    if (!categoryId) {
      setBrandList([]);
      setProductList([]);
      setBrandId("");
      setProductId("");
      return;
    }
    setFetchingBrand(true);
    api
      .get(`/brand/?categoryId=${categoryId}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          // ✅ Deduplicate brands by ID
          const uniqueBrands = (res.data.data || []).filter(
            (brand, index, self) =>
              index === self.findIndex((b) => b.id === brand.id),
          );
          setBrandList(uniqueBrands);
        }
      })
      .catch(() => setBrandList([]))
      .finally(() => setFetchingBrand(false));
  }, [categoryId]);

  // ✅ Fetch Products when Brand changes
  useEffect(() => {
    if (!brandId) {
      setProductList([]);
      setProductId("");
      return;
    }

    setFetchingProduct(true);
    api
      .get(
        `/product/?categoryId=${categoryId}&brandId=${brandId}`,
        adminReqConfig(),
      )
      .then((res) => {
        if (res.status === 200) {
          const products = res.data.data || [];

          // ✅ FIX: Use Set for more reliable deduplication by ID
          const seenIds = new Set();
          const uniqueProducts = products.filter((product) => {
            if (seenIds.has(product.productId)) {
              return false; 
            }
            seenIds.add(product.productId);
            return true;
          });

          // ✅ Also sort by name for better UX
          uniqueProducts.sort((a, b) => a.name.localeCompare(b.name));

          setProductList(uniqueProducts);
        }
      })
      .catch(() => setProductList([]))
      .finally(() => setFetchingProduct(false));
  }, [brandId]);

  const B2B_TYPES = [
    "dealer",
    "sub-dealer",
    "contractor",
    "builder",
    "engineer",
    "architect",
    "others",
  ];
  const B2C_TYPES = ["individual"];

  // ✅ Auto-select customer type when group changes
  useEffect(() => {
    if (customerGroup === "all") {
      setSelectedCustomerType("");
    } else if (customerGroup === "b2b") {
      setSelectedCustomerType(B2B_TYPES[0]);
    } else if (customerGroup === "b2c") {
      setSelectedCustomerType(B2C_TYPES[0]);
    } else if (customerGroup === "") {
      setSelectedCustomerType("");
    }
  }, [customerGroup]);

  // ✅ Update customerGroup when type changes
  useEffect(() => {
    if (!selectedCustomerType) return;
    if (B2B_TYPES.includes(selectedCustomerType)) {
      setCustomerGroup("b2b");
    } else if (B2C_TYPES.includes(selectedCustomerType)) {
      setCustomerGroup("b2c");
    }
  }, [selectedCustomerType]);

  // ✅ Fetch Notification Details for Edit/View Mode
  const fetchNotificationDetails = (id) => {
    api
      .get(`/notification/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const notif = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (!notif) return;

          setTitle(notif.title || "");
          setBody(notif.body || "");
          setDisplay(notif.display || "");

          // ✅ Load Category/Brand/Product (Single Select)
          setCategoryId(notif.categoryId || "");
          setBrandId(notif.brandId || "");
          setProductId(notif.productId || "");

          // ✅ Load Customer Type
          const targetValue = notif.target || "";
          if (targetValue === "all") {
            setCustomerGroup("all");
            setSelectedCustomerType("");
          } else {
            const targetArray = targetValue
              .split(",")
              .map((t) => t.trim())
              .filter((t) => t);
            if (targetArray.length > 0) {
              setSelectedCustomerType(targetArray[0]);
              if (B2B_TYPES.includes(targetArray[0])) {
                setCustomerGroup("b2b");
              } else if (B2C_TYPES.includes(targetArray[0])) {
                setCustomerGroup("b2c");
              }
            }
          }

          // ✅ Load Image
          if (notif.imageUrl) {
            setMainImagePreview(baseURL + "/" + notif.imageUrl);
            setImageFile(notif.imageUrl);
            setOriginalImageUrl(notif.imageUrl);
            setImageChanged(false);
          }
        }
      })
      .catch(() => {
        toast({
          type: "error",
          message: "Failed to load notification details.",
        });
      });
  };

  useEffect(() => {
    if (notificationId) {
      fetchNotificationDetails(notificationId);
    }
  }, [notificationId]);

  // ✅ Image Handler (UNCHANGED)
  const handleMainImage = (files) => {
    if (saving || !files || files.length === 0) return;
    const file = files[0];
    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type,
      )
    ) {
      toast({ type: "error", message: "Only JPG, PNG, WebP allowed" });
      if (mainImageElement.current) mainImageElement.current.value = "";
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast({ type: "error", message: "Image must be under 3MB" });
      if (mainImageElement.current) mainImageElement.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImagePreview(reader.result);
      setImageFile(file);
      setImageChanged(true);
    };
    reader.readAsDataURL(file);
    if (mainImageElement.current) mainImageElement.current.value = "";
  };

  // ✅ Validation (Updated for Category/Brand/Product)
  const validate = () => {
    if (!title.trim()) {
      toast({ type: "error", message: "Title is required" });
      return false;
    }
    if (!body.trim()) {
      toast({ type: "error", message: "Message body is required" });
      return false;
    }
    if (!display) {
      toast({ type: "error", message: "Please select where to display" });
      return false;
    }
    if (!customerGroup) {
      toast({ type: "error", message: "Please select customer type" });
      return false;
    }
    if (customerGroup !== "all" && !selectedCustomerType) {
      toast({ type: "error", message: "Please select a customer type" });
      return false;
    }
    return true;
  };

  // ✅ Save Handler (Updated with Category/Brand/Product)
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("body", body.trim());
      formData.append("status", status ? "active" : "inactive");
      formData.append("display", display);
      formData.append("source", "manual");

      // ✅ Append Category/Brand/Product (Single Select - Send ID)
      if (categoryId) formData.append("categoryId", categoryId);
      if (brandId) formData.append("brandId", brandId);
      if (productId) formData.append("productId", productId);

      // ✅ Customer Type
      if (customerGroup === "all") {
        formData.append("target", "all");
      } else {
        formData.append("target", selectedCustomerType);
      }

      // ✅ Image
      if (imageChanged && imageFile instanceof File) {
        formData.append("imageFile", imageFile);
      } else if (isEditMode && originalImageUrl) {
        formData.append("existingImage", originalImageUrl);
      }

      let url = "/notification/";
      if (isEditMode) {
        url += `?id=${notificationId}`;
      }

      const res = await api.post(url, formData, {
        ...adminReqConfig(),
        headers: { ...adminReqConfig().headers, "Content-Type": undefined },
      });

      if (![200, 201].includes(res.status)) throw new Error("Save failed");

      toast({
        type: "success",
        message: isEditMode
          ? "Notification updated successfully!"
          : "Notification added successfully!",
      });
      navigate("/admin/notification/notificationList");
      if (!isEditMode) clearFields();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (isEditMode
          ? "Failed to update notification"
          : "Failed to add notification");
      toast({ type: "error", message: msg });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ✅ Clear Fields (Updated)
  const clearFields = () => {
    if (saving) return;
    setTitle("");
    setBody("");
    setDisplay("");
    setCategoryId("");
    setBrandId("");
    setProductId("");
    setCustomerGroup("");
    setSelectedCustomerType("");
    setImageFile(null);
    setMainImagePreview("");
    setStatus(true);
    setImageChanged(false);
  };

  // ✅ Input Style (UNCHANGED)
  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 24px",
    backgroundColor: "transparent !important",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
    opacity: saving ? 0.7 : 1,
    pointerEvents: saving ? "none" : "auto",
  };

  // ✅ Helper Functions (UNCHANGED)
  const getCustomerTypeOptions = () => [
    { value: "dealer", label: "Dealer" },
    { value: "sub-dealer", label: "Sub-Dealer" },
    { value: "contractor", label: "Contractor" },
    { value: "builder", label: "Builder" },
    { value: "engineer", label: "Engineer" },
    { value: "architect", label: "Architect" },
    { value: "individual", label: "Individual" },
    { value: "others", label: "Others" },
  ];

  const getDisplayText = (displayValue) => {
    if (displayValue === "web") return "Website";
    if (displayValue === "app") return "Mobile";
    if (displayValue === "all") return "All";
    return displayValue || "N/A";
  };

  const getCustomerTypeDisplayText = (targetValue) => {
    if (targetValue === "all") return "All";
    if (!targetValue) return "N/A";
    const option = getCustomerTypeOptions().find(
      (opt) => opt.value === targetValue,
    );
    return option ? option.label : targetValue;
  };

  // ✅ Filter parent categories (parentId !== "-1")
  const parentCategories = categoryList.filter(
    (cat) => String(cat.parentId) !== "-1",
  );

  const getProductId = (product) => product.id;

  return (
    <>
      {isViewMode ? (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title="NOTIFICATION DETAILS"
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "75px",
                marginBottom: "50px",
                height: "100vh",
              }}
            >
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-3 mb-4">
                    <div
                      className="card p-8"
                      style={{ height: "300px", border: "none" }}
                    >
                      <div className="card-body d-flex flex-column align-items-start">
                        <div className="mb-3" style={{ textAlign: "center" }}>
                          {mainImagePreview ? (
                            <img
                              src={mainImagePreview}
                              alt="Notification"
                              className="img-fluid rounded"
                              style={{
                                width: "190px",
                                height: "190px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <div
                              className="bg-light d-flex justify-content-center align-items-center"
                              style={{
                                width: "190px",
                                height: "190px",
                                borderRadius: "8px",
                              }}
                            >
                              <i
                                className="fa fa-bell text-muted"
                                style={{ fontSize: "48px" }}
                              ></i>
                            </div>
                          )}
                        </div>
                        <h6
                          className="card-title fs-5 mt-16"
                          style={{ textAlign: "left", width: "100%" }}
                        >
                          {title || "Notification"}
                        </h6>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-9">
                    <div className="card mb-22 pb-40 border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">Notification Details</h6>
                      </div>
                      <div className="card-body" style={{ height: "200px" }}>
                        <div className="row px-16">
                          <p>
                            <strong>Display On:</strong>{" "}
                            {getDisplayText(display)}
                          </p>
                          <p>
                            <strong>Customer Type:</strong>{" "}
                            {getCustomerTypeDisplayText(
                              customerGroup === "all"
                                ? "all"
                                : selectedCustomerType,
                            )}
                          </p>
                          {/* ✅ Show Category/Brand/Product if selected */}
                          {categoryId && (
                            <p>
                              <strong>Category:</strong>{" "}
                              {categoryList.find((c) => c.id == categoryId)
                                ?.name || "N/A"}
                            </p>
                          )}
                          {brandId && (
                            <p>
                              <strong>Brand:</strong>{" "}
                              {brandList.find((b) => b.id == brandId)?.name ||
                                "N/A"}
                            </p>
                          )}
                          {productId && (
                            <p>
                              <strong>Product:</strong>{" "}
                              {productList.find(
                                (p) => getProductId(p) == productId,
                              )?.name || "N/A"}
                            </p>
                          )}
                          <p>
                            <strong>Message:</strong> {body || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      ) : (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title={isEditMode ? "EDIT NOTIFICATION" : "NEW NOTIFICATION"}
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "70px",
                marginBottom: "40px",
              }}
            >
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">Display Information</h6>
                        <button
                          onClick={clearFields}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Name</h6>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Name"
                              value={title}
                              onChange={(e) =>
                                !saving && setTitle(e.target.value)
                              }
                              disabled={saving}
                              style={inputStyle}
                            />
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Display On
                            </h6>
                            <select
                              className="form-select"
                              value={display}
                              onChange={(e) =>
                                !saving && setDisplay(e.target.value)
                              }
                              disabled={saving}
                              style={inputStyle}
                            >
                              <option value="">Select</option>
                              <option value="all">All</option>
                              <option value="web">Website</option>
                              <option value="app">Mobile</option>
                            </select>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Customer Type
                            </h6>
                            <select
                              className="form-select"
                              value={customerGroup}
                              onChange={(e) =>
                                !saving && setCustomerGroup(e.target.value)
                              }
                              disabled={saving}
                              style={inputStyle}
                            >
                              <option value="">Select</option>
                              <option value="all">All</option>
                              <option value="b2b">B2B</option>
                              <option value="b2c">B2C</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Category</h6>
                            <select
                              className="form-select"
                              value={categoryId}
                              onChange={(e) =>
                                !saving && setCategoryId(e.target.value)
                              }
                              disabled={saving || fetchingCategory}
                              style={inputStyle}
                            >
                              <option value="">Select Category</option>
                              {parentCategories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Brand</h6>
                            <select
                              className="form-select"
                              value={brandId}
                              onChange={(e) =>
                                !saving && setBrandId(e.target.value)
                              }
                              disabled={saving || fetchingBrand || !categoryId}
                              style={inputStyle}
                            >
                              <option value="">
                                {categoryId
                                  ? "Select Brand"
                                  : "Select Category First"}
                              </option>
                              {brandList.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                  {brand.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Product</h6>
                            <select
                              className="form-select"
                              value={productId}
                              onChange={(e) =>
                                !saving && setProductId(e.target.value)
                              }
                              disabled={saving || fetchingProduct || !brandId}
                              style={inputStyle}
                            >
                              <option value="">
                                {brandId
                                  ? "Select Product"
                                  : "Select Brand First"}
                              </option>
                              {productList.map((product) => (
                                // ✅ Use product.id directly for key and value
                                <option key={product.productId} value={product.productId}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* ✅ Show radio buttons ONLY if customerGroup is NOT "all" */}
                        {customerGroup && customerGroup !== "all" && (
                          <>
                            <h6 className="text-md mb-16 fw-bold">
                              Select Customer Group
                            </h6>
                            <div
                              className="row ms-5 me-5 mt-16 mb-40 p-16"
                              style={{
                                backgroundColor: "#F1F4F9",
                                borderRadius: "6px",
                                opacity: saving ? 0.7 : 1,
                                pointerEvents: saving ? "none" : "auto",
                              }}
                            >
                              {getCustomerTypeOptions()
                                .filter((opt) => {
                                  if (customerGroup === "b2b")
                                    return B2B_TYPES.includes(opt.value);
                                  if (customerGroup === "b2c")
                                    return B2C_TYPES.includes(opt.value);
                                  return true;
                                })
                                .map((opt) => (
                                  <CustomerTypeRadio
                                    key={opt.value}
                                    label={opt.label}
                                    value={opt.value}
                                    isSelected={
                                      selectedCustomerType === opt.value
                                    }
                                    onSelect={() =>
                                      !saving &&
                                      setSelectedCustomerType(opt.value)
                                    }
                                    disabled={saving}
                                  />
                                ))}
                            </div>
                          </>
                        )}

                        <div className="mb-16">
                          <h6 className="text-md mb-16 fw-bold">
                            Message Body
                          </h6>
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Enter notification message"
                            value={body}
                            onChange={(e) => !saving && setBody(e.target.value)}
                            disabled={saving}
                            style={{ ...inputStyle, padding: "10px 24px" }}
                          />
                        </div>

                        <h6 className="mb-0 fs-5">
                          {isEditMode ? "Edit Image" : "Upload Image"}
                        </h6>
                        <div className="card-body" style={{ height: "300px" }}>
                          <div
                            className="border-dashed text-center cursor-pointer"
                            onClick={() =>
                              !saving && mainImageElement.current?.click()
                            }
                            style={{
                              border: "2px dashed #ccc",
                              borderRadius: "8px",
                              minHeight: "270px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              opacity: saving ? 0.6 : 1,
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                          >
                            {saving ? (
                              <>
                                <i
                                  className="fa fa-spinner fa-spin mb-3"
                                  style={{ fontSize: "48px", color: "#9e9e9e" }}
                                ></i>
                                <h6>Processing...</h6>
                                <small className="text-muted">
                                  Please wait while saving...
                                </small>
                              </>
                            ) : mainImagePreview ? (
                              <div className="d-flex flex-column align-items-center w-100">
                                <img
                                  src={mainImagePreview}
                                  alt="Main"
                                  className="img-fluid mb-2"
                                  style={{
                                    maxHeight: "180px",
                                    objectFit: "contain",
                                  }}
                                />
                                <small className="text-muted">
                                  Current image
                                </small>
                              </div>
                            ) : (
                              <>
                                <i
                                  className="fa fa-cloud-upload-alt mb-3"
                                  style={{
                                    fontSize: "48px",
                                    color: "#0d6efd",
                                  }}
                                ></i>
                                <h6>
                                  Drop Your Images Here, Or{" "}
                                  <span className="text-primary">
                                    Click To Browse
                                  </span>
                                </h6>
                                <small className="text-muted">
                                  Square Pixels Recommended. PNG, JPG, WebP
                                  allowed.
                                </small>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            onChange={(e) =>
                              !saving && handleMainImage(e.target.files)
                            }
                            className="d-none"
                            accept="image/*"
                            ref={mainImageElement}
                            disabled={saving}
                          />
                        </div>

                        <div className="d-flex justify-content-end gap-8">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            style={getButtonStyle(true)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                {isEditMode ? "Updating..." : "Adding..."}
                              </>
                            ) : isEditMode ? (
                              "Update"
                            ) : (
                              "Add"
                            )}
                          </button>
                          <button
                            onClick={clearFields}
                            disabled={saving}
                            style={getButtonStyle(false)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Clearing...
                              </>
                            ) : (
                              "Clear"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      )}
    </>
  );
};

export default AddNotification;
